"use client"

import { useState, useEffect, useMemo } from "react"
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
  Button,
  Divider,
  Chip,
  Grid,
  useTheme,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  alpha,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"
import PersonIcon from "@mui/icons-material/Person"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"

// Función helper para búsqueda flexible por palabras
const matchesSearch = (searchText, descripcion, apellido) => {
  // Normalizar el texto de búsqueda: eliminar espacios al inicio/final y normalizar espacios múltiples
  const normalizedSearch = searchText.trim().toLowerCase().replace(/\s+/g, ' ')
  
  if (!normalizedSearch) return true
  
  // Normalizar los campos de búsqueda - construir el nombre completo igual que en getOptionLabel
  const desc = (descripcion || '').toLowerCase().trim()
  const apell = (apellido || '').toLowerCase().trim()
  // Construir el nombre completo igual que en getOptionLabel: descripcion + (apellido ? ` ${apellido}` : "")
  const nombreCompleto = apell ? `${desc} ${apell}`.trim() : desc
  
  // Primero verificar si el texto completo está contenido (búsqueda exacta)
  if (nombreCompleto.includes(normalizedSearch)) {
    return true
  }
  
  // Búsqueda flexible: eliminar espacios del texto de búsqueda y del nombre completo
  // para permitir búsquedas como "virginia c" que coincidan con "virginia coraza"
  const searchSinEspacios = normalizedSearch.replace(/\s+/g, '')
  const nombreSinEspacios = nombreCompleto.replace(/\s+/g, '')
  
  // Si el texto sin espacios está contenido en el nombre sin espacios, coincide
  if (nombreSinEspacios.includes(searchSinEspacios)) {
    return true
  }
  
  // Búsqueda por palabras: dividir el texto de búsqueda en palabras
  const palabrasBusqueda = normalizedSearch.split(/\s+/).filter(p => p.length > 0)
  const palabrasNombre = nombreCompleto.split(/\s+/).filter(p => p.length > 0)
  
  // Verificar que todas las palabras de búsqueda coincidan con alguna palabra del nombre
  // Una palabra de búsqueda coincide si:
  // 1. Alguna palabra del nombre empieza con esa palabra (prefijo)
  // 2. O la palabra de búsqueda está contenida en alguna palabra del nombre
  return palabrasBusqueda.every(palabraBusqueda => {
    return palabrasNombre.some(palabraNombre => {
      // Verificar si la palabra del nombre empieza con la palabra de búsqueda
      if (palabraNombre.startsWith(palabraBusqueda)) {
        return true
      }
      // O si la palabra de búsqueda está contenida en la palabra del nombre
      if (palabraNombre.includes(palabraBusqueda)) {
        return true
      }
      return false
    })
  })
}

const ModalAsociarItemPorCliente = ({ open, handleClose, entidades, onAssociate }) => {
  const theme = useTheme()
  const [searchSource, setSearchSource] = useState("")
  const [searchTarget, setSearchTarget] = useState("")
  const [sourceId, setSourceId] = useState(null)
  const [selectedTargetIds, setSelectedTargetIds] = useState([])

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setSearchSource("")
      setSearchTarget("")
      setSourceId(null)
      setSelectedTargetIds([])
    }
  }, [open])

  // Normalizar entidades: eliminar duplicados por ID
  const entidadesUnicas = useMemo(() => {
    // Asegurar que entidades es un array
    const entidadesArray = Array.isArray(entidades) ? entidades : []
    const seen = new Set()
    return entidadesArray.filter((e) => {
      if (!e || !e.id) return false
      if (seen.has(e.id)) return false
      seen.add(e.id)
      return true
    })
  }, [entidades])

  // Filter opciones de origen (excluye entidades seleccionadas como destino)
  const filteredSource = useMemo(
    () => {
      if (!searchSource.trim()) {
        return entidadesUnicas.filter((e) => !selectedTargetIds.includes(e.id))
      }
      
      return entidadesUnicas.filter(
        (e) => {
          if (selectedTargetIds.includes(e.id)) return false
          return matchesSearch(searchSource, e.descripcion, e.apellido)
        }
      )
    },
    [entidadesUnicas, selectedTargetIds, searchSource]
  )

  // Entidades disponibles para destino (excluye origen y ya seleccionadas)
  const availableTargetEntidades = useMemo(
    () => {
      if (!searchTarget.trim()) {
        return entidadesUnicas.filter((e) => e.id !== sourceId && !selectedTargetIds.includes(e.id))
      }
      
      return entidadesUnicas.filter(
        (e) => {
          if (e.id === sourceId) return false
          if (selectedTargetIds.includes(e.id)) return false
          return matchesSearch(searchTarget, e.descripcion, e.apellido)
        }
      )
    },
    [entidadesUnicas, sourceId, selectedTargetIds, searchTarget]
  )

  // Entidades seleccionadas como destino
  const selectedTargetEntidades = useMemo(
    () => {
      return entidadesUnicas.filter((e) => selectedTargetIds.includes(e.id))
    },
    [entidadesUnicas, selectedTargetIds]
  )

  // Agregar entidad a la lista de destino
  const handleAddTarget = (entidad) => {
    if (!selectedTargetIds.includes(entidad.id)) {
      setSelectedTargetIds((prev) => [...prev, entidad.id])
    }
  }

  // Quitar entidad de la lista de destino
  const handleRemoveTarget = (entidad) => {
    setSelectedTargetIds((prev) => prev.filter((id) => id !== entidad.id))
  }

  const handleAssociateClick = () => {
    if (!sourceId || selectedTargetIds.length === 0) {
      alert("Debe seleccionar una entidad origen y al menos una entidad destino.")
      return
    }
    onAssociate(sourceId, selectedTargetIds)
    handleClose()
  }

  const sourceEntity = entidadesUnicas.find((e) => e.id === sourceId)

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="modal-asociar-precios">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: 700, md: 900 },
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ContentCopyIcon color="primary" />
            Asociar Precios entre Clientes
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3, overflow: "auto", flex: 1 }}>
          {/* Sección Entidad Origen */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Entidad a copiar precios
            </Typography>
            <Autocomplete
              options={filteredSource}
              getOptionLabel={(e) =>
                e.descripcion + (e.apellido ? ` ${e.apellido}` : "")
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={sourceEntity || null}
              onChange={(_, val) => setSourceId(val ? val.id : null)}
              inputValue={searchSource}
              onInputChange={(_, val) => setSearchSource(val)}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField {...params} label="Buscar entidad origen" size="small" />
              )}
              fullWidth
            />
            {sourceEntity && (
              <Chip
                icon={<PersonIcon />}
                label={`Copiar de: ${sourceEntity.descripcion}${
                  sourceEntity.apellido ? ` ${sourceEntity.apellido}` : ""
                }`}
                color="primary"
                variant="outlined"
                sx={{ mt: 1 }}
              />
            )}
          </Box>

          <Divider sx={{ my: 2 }}>
            <Chip label="Entidades Destino" />
          </Divider>

          {/* Sistema de transferencia de entidades */}
          <Grid container spacing={2}>
            {/* Lista de entidades disponibles */}
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" gutterBottom>
                Disponibles ({availableTargetEntidades.length})
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="Buscar entidades..."
                value={searchTarget}
                onChange={(e) => setSearchTarget(e.target.value)}
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Paper
                variant="outlined"
                sx={{
                  height: 300,
                  overflow: "auto",
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                }}
              >
                <List dense component="div" role="list">
                  {availableTargetEntidades.length > 0 ? (
                    availableTargetEntidades.map((entidad) => (
                      <ListItem
                        key={entidad.id}
                        role="listitem"
                        button
                        onDoubleClick={() => handleAddTarget(entidad)}
                        sx={{
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PersonIcon fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={entidad.descripcion}
                          secondary={entidad.apellido}
                          primaryTypographyProps={{ variant: "body2" }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem sx={{ justifyContent: "center", py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {searchTarget ? "No se encontraron resultados" : "No hay entidades disponibles"}
                      </Typography>
                    </ListItem>
                  )}
                </List>
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Doble clic para agregar
              </Typography>
            </Grid>

            {/* Botones de transferencia */}
            <Grid
              item
              xs={12}
              md={2}
              sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
            >
              <IconButton
                onClick={() => {
                  if (availableTargetEntidades.length > 0) {
                    handleAddTarget(availableTargetEntidades[0])
                  }
                }}
                disabled={availableTargetEntidades.length === 0}
                sx={{
                  mb: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <ArrowForwardIcon />
              </IconButton>

              <IconButton
                onClick={() => {
                  if (selectedTargetEntidades.length > 0) {
                    handleRemoveTarget(selectedTargetEntidades[0])
                  }
                }}
                disabled={selectedTargetEntidades.length === 0}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Grid>

            {/* Lista de entidades seleccionadas */}
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" gutterBottom>
                Seleccionadas ({selectedTargetEntidades.length})
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  height: 345,
                  overflow: "auto",
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  borderColor: selectedTargetEntidades.length > 0 ? alpha(theme.palette.primary.main, 0.3) : undefined,
                }}
              >
                <List dense component="div" role="list">
                  {selectedTargetEntidades.length > 0 ? (
                    selectedTargetEntidades.map((entidad) => (
                      <ListItem
                        key={entidad.id}
                        role="listitem"
                        button
                        onDoubleClick={() => handleRemoveTarget(entidad)}
                        sx={{
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <PersonIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={entidad.descripcion}
                          secondary={entidad.apellido}
                          primaryTypographyProps={{ variant: "body2" }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem sx={{ justifyContent: "center", py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        No hay entidades seleccionadas
                      </Typography>
                    </ListItem>
                  )}
                </List>
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Doble clic para quitar
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!sourceId || selectedTargetIds.length === 0}
            onClick={handleAssociateClick}
            startIcon={<ContentCopyIcon />}
          >
            Asociar ({selectedTargetIds.length})
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default ModalAsociarItemPorCliente
